/**
 * Renders a Window Generator payload to a PNG blob for email/storage.
 * Uses React render + SVG -> Image -> Canvas to produce a PNG.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { WindowGeneratorBaySchematicSvg, WindowGeneratorSvg, DESIGN_CANVAS_BG } from '../components/windowGeneratorRender';
import type { WindowGeneratorPayload } from '../services/windowGeneratorTemplates.service';

const EXPORT_SIZE_PX = 400;
const EXPORT_BAY_SCHEMATIC_WIDTH_PX = 420;
type WindowGeneratorExportView = 'external' | 'internal';
type WindowGeneratorExportMode = 'design' | 'presentation';

/**
 * Renders the payload to an SVG string by mounting the component and reading the DOM.
 */
function renderPayloadToSvgString(
  payload: WindowGeneratorPayload,
  view: WindowGeneratorExportView,
  mode: WindowGeneratorExportMode,
  widthPx: number = EXPORT_SIZE_PX
): string {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  try {
    const root = createRoot(container);
    try {
      flushSync(() => {
        root.render(React.createElement(WindowGeneratorSvg, { payload, widthPx, view, mode }));
      });
      const svg = container.querySelector('svg');
      return svg ? svg.outerHTML : '';
    } finally {
      root.unmount();
    }
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container);
  }
}

function renderBaySchematicToSvgString(payload: WindowGeneratorPayload): string {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  try {
    const root = createRoot(container);
    try {
      flushSync(() => {
        root.render(React.createElement(WindowGeneratorBaySchematicSvg, { payload, widthPx: EXPORT_BAY_SCHEMATIC_WIDTH_PX }));
      });
      const svg = container.querySelector('svg');
      return svg ? svg.outerHTML : '';
    } finally {
      root.unmount();
    }
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container);
  }
}

const inlinedImageCache = new Map<string, Promise<string | null>>();

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });
}

function fetchImageAsDataUrl(href: string): Promise<string | null> {
  let pending = inlinedImageCache.get(href);
  if (!pending) {
    pending = fetch(href)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => (b ? blobToDataUrl(b) : null))
      .catch(() => null);
    inlinedImageCache.set(href, pending);
    // Don't keep failures cached; a later export may succeed.
    pending.then((v) => { if (!v) inlinedImageCache.delete(href); });
  }
  return pending;
}

/**
 * An SVG loaded via <img> never fetches external resources, so <image href="/images/...">
 * (e.g. patterned glass tiles) would render blank. Inline them as data URIs first.
 */
export async function inlineExternalImages(svgString: string): Promise<string> {
  const hrefRe = /(<image\b[^>]*?\s(?:xlink:)?href=")([^"]+)(")/g;
  const hrefs = new Set<string>();
  for (const m of svgString.matchAll(hrefRe)) {
    if (!m[2].startsWith('data:')) hrefs.add(m[2]);
  }
  if (!hrefs.size) return svgString;
  const resolved = new Map<string, string>();
  await Promise.all(Array.from(hrefs).map(async (href) => {
    // outerHTML escapes & as &amp; inside attributes
    const dataUrl = await fetchImageAsDataUrl(href.replace(/&amp;/g, '&'));
    if (dataUrl) resolved.set(href, dataUrl);
  }));
  return svgString.replace(hrefRe, (all, pre, href, post) => {
    const dataUrl = resolved.get(href);
    return dataUrl ? `${pre}${dataUrl}${post}` : all;
  });
}

/**
 * Converts an SVG string to a PNG blob via canvas.
 */
function svgStringToPngBlob(svgString: string, mode: WindowGeneratorExportMode = 'design'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Canvas 2d context not available'));
          return;
        }
        ctx.fillStyle = DESIGN_CANVAS_BG; // consistent canvas for design and presentation
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (b) => {
            URL.revokeObjectURL(url);
            if (b) resolve(b);
            else reject(new Error('Failed to create PNG blob'));
          },
          'image/png',
          0.92
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG as image'));
    };
    img.src = url;
  });
}

/**
 * Renders a Window Generator payload to a PNG blob (for upload to storage or email).
 */
export async function renderWindowGeneratorPayloadToPngBlob(
  payload: WindowGeneratorPayload,
  opts?: { view?: WindowGeneratorExportView; mode?: WindowGeneratorExportMode; widthPx?: number }
): Promise<Blob> {
  if (!payload?.root || payload.w == null || payload.h == null) {
    throw new Error('Invalid Window Generator payload');
  }
  const view: WindowGeneratorExportView = opts?.view === 'internal' ? 'internal' : 'external';
  const mode: WindowGeneratorExportMode = opts?.mode === 'presentation' ? 'presentation' : 'design';
  const widthPx = Number.isFinite(Number(opts?.widthPx)) ? Math.max(240, Math.round(Number(opts?.widthPx))) : EXPORT_SIZE_PX;
  const svgString = renderPayloadToSvgString(payload, view, mode, widthPx);
  if (!svgString) throw new Error('Failed to render payload to SVG');
  return svgStringToPngBlob(await inlineExternalImages(svgString), mode);
}

export async function renderWindowGeneratorBaySchematicToPngBlob(
  payload: WindowGeneratorPayload
): Promise<Blob> {
  if (!payload?.root || payload.w == null || payload.h == null) {
    throw new Error('Invalid Window Generator payload');
  }
  const svgString = renderBaySchematicToSvgString(payload);
  if (!svgString) throw new Error('Failed to render bay schematic to SVG');
  return svgStringToPngBlob(await inlineExternalImages(svgString), 'design');
}
